import { randomUuid } from "@models/utils/uuid";
import { Emitter } from "@core/events/emitter";
import type {
  ConfirmationArgs,
  ConfirmationRequest,
  ConfirmationResult,
  IConfirmationModel,
} from "./IConfirmationModel";

/**
 * `ConfirmationModel` — see {@link IConfirmationModel}. In-memory queue; the
 * only persisted state is the "Don't show this again" flag, which mirrors
 * Unity's `PlayerPrefs.SetInt(key, 1)` in `localStorage` under the same key
 * so a key shared with the desktop app stays recognisable.
 */
export class ConfirmationModel implements IConfirmationModel {
  readonly onChanged = new Emitter<ReadonlyArray<ConfirmationRequest>>();

  private m_requests: ReadonlyArray<ConfirmationRequest> = [];

  get requests(): ReadonlyArray<ConfirmationRequest> {
    return this.m_requests;
  }

  request(args: ConfirmationArgs): Promise<ConfirmationResult> {
    // A suppressed dialog auto-confirms: Unity never navigates when the
    // PlayerPrefs flag is set, and every caller that uses a key treats
    // "already answered" as the affirmative path.
    if (args.doNotShowAgainKey && this.isSuppressed(args.doNotShowAgainKey)) {
      return Promise.resolve({ confirmed: true, additionalAnswer: false });
    }
    return new Promise<ConfirmationResult>((resolve) => {
      const request: ConfirmationRequest = {
        id: randomUuid(),
        args,
        resolve,
      };
      this.m_requests = [...this.m_requests, request];
      this.onChanged.emit(this.m_requests);
    });
  }

  async confirm(args: ConfirmationArgs): Promise<boolean> {
    const result = await this.request(args);
    return result.confirmed === true;
  }

  answer(id: string, result: ConfirmationResult): void {
    const request = this.m_requests.find((r) => r.id === id);
    if (!request) return;
    this.m_requests = this.m_requests.filter((r) => r.id !== id);
    this.onChanged.emit(this.m_requests);
    request.resolve(result);
  }

  isSuppressed(key: string): boolean {
    if (!key) return false;
    try {
      return window.localStorage.getItem(key) === "1";
    } catch {
      // Private-mode / disabled storage: never suppress rather than throw.
      return false;
    }
  }

  suppress(key: string): void {
    if (!key) return;
    try {
      window.localStorage.setItem(key, "1");
      // Also record the key in an index. `doNotShowAgainKey` is caller-supplied
      // and deliberately un-namespaced (it has to match the `PlayerPrefs` key
      // Unity writes), so without this there is no way to enumerate the
      // suppressions later — which app settings' "Clear Cache" needs in order
      // to reset them, the way Unity's `ClearReleaseNotesPrefs` resets its own
      // one-shot dismissals.
      const index = new Set(readSuppressedIndex());
      if (!index.has(key)) {
        index.add(key);
        window.localStorage.setItem(K_SUPPRESSED_INDEX, JSON.stringify([...index]));
      }
    } catch {
      // Best-effort, same as above.
    }
  }
}

/** Index of every key {@link ConfirmationModel.suppress} has written. */
export const K_SUPPRESSED_INDEX = "Campfire.Confirmation.SuppressedKeys";

/** Keys currently suppressed, per the index. Tolerates a corrupt/absent entry. */
export function readSuppressedIndex(): ReadonlyArray<string> {
  try {
    const raw = window.localStorage.getItem(K_SUPPRESSED_INDEX);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
}
